import { ColumnsType } from "antd/es/table";
import { useMemo } from "react";
import { DATE_FORMAT, PAYMENT_METHODS } from "../constants";
import dayjs from "dayjs";
import { Tooltip } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { getFormattedCurrency } from "../currencyFormatter";
import { useGetRole } from "./getRole";

export const useDefaultPaymentHistoryColumns = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, isAdmin] = useGetRole();

  const tooltipStyle: any = useMemo(
    () => ({
      maxWidth: "300px"
    }),
    []
  );

  const columns: ColumnsType<any> = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: 100,
        fixed: "left",
        ellipsis: true,
        sorter: true,
        render: (value: string | Date, record: any) => {
          const { onHold } = record;

          return (
            <div className="">
              <span>{dayjs(value).format(DATE_FORMAT)}</span>
              {onHold && (
                <Tooltip
                  className="ml-2"
                  overlayClassName="text-sm"
                  overlayStyle={tooltipStyle}
                  title={
                    isAdmin
                      ? "Release is held"
                      : "Any payment received over $5,000.00 in the form of a personal check will be held for a period of 5-7 business days before they are released"
                  }
                  trigger={["hover"]}
                >
                  <ClockCircleOutlined className="warning" />
                </Tooltip>
              )}
            </div>
          );
        }
      },
      {
        title: "Due Date",
        dataIndex: "dueDate",
        key: "dueDate",
        width: 78,
        fixed: "left",
        ellipsis: true,
        render: (value: string | Date) => (value ? dayjs(value).format(DATE_FORMAT) : "-")
      },
      {
        title: "Interest To",
        dataIndex: "interestDateTo",
        key: "interestDateTo",
        width: 118,
        render: (value: string | Date) => (value ? dayjs(value).format(DATE_FORMAT) : "-")
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        width: 108,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Method",
        dataIndex: "method",
        key: "method",
        width: 92,
        ellipsis: true,
        render: (value: number, record: any) => {
          const { reversal } = record;

          return (PAYMENT_METHODS.find((method) => method.value === value)?.label || "-") + (reversal ? " (R)" : "");
        }
      },
      {
        title: "Principal Paid",
        dataIndex: "principalPaid",
        key: "principalPaid",
        width: 108,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Principal Balance",
        dataIndex: "principalBalance",
        key: "principalBalance",
        width: 96,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Interest Calculated",
        dataIndex: "interestCalculated",
        key: "interestCalculated",
        width: 108,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Interest Paid",
        dataIndex: "interestPaid",
        key: "interestPaid",
        width: 108,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Unpaid Interest Balance",
        dataIndex: "unpaidInterest",
        key: "unpaidInterest",
        width: 112,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "To Impound",
        dataIndex: "toImpound",
        key: "toImpound",
        width: 108,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "From Impound",
        dataIndex: "fromImpound",
        key: "fromImpound",
        width: 112,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Impound Balance",
        dataIndex: "impoundBalance",
        key: "impoundBalance",
        width: 112,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Mortgage/Passthru",
        dataIndex: "mortgage",
        key: "mortgage",
        width: 128,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Late Charges Calculated",
        dataIndex: "lateChargesCalculated",
        key: "lateChargesCalculated",
        width: 144,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Late Charges Paid",
        dataIndex: "lateChargesPaid",
        key: "lateChargesPaid",
        width: 136,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Late Charges to Principle",
        dataIndex: "lateChargeToPrinciple",
        key: "lateChargeToPrinciple",
        width: 160,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Unpaid Late Charge Balance",
        dataIndex: "unpaidLateChargeBalance",
        key: "unpaidLateChargeBalance",
        width: 142,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Payer Fees",
        dataIndex: "payerFees",
        key: "payerFees",
        width: 108,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Payee Fees",
        dataIndex: "payeeFees",
        key: "payeeFees",
        width: 108,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      },
      {
        title: "Principal Adjustment",
        dataIndex: "principalAdjustment",
        key: "principalAdjustment",
        width: 116,
        ellipsis: true,
        align: "right",
        render: (value: number) => getFormattedCurrency(value || 0)
      }
    ],
    [isAdmin, tooltipStyle]
  );

  return { columns };
};
